import {
  cellRulesFromRevision,
  panelCellRuleMarkerIds,
  revisionsForPanel,
} from 'components/Pages/OperationsSettings/CellRulesDataGrid/cellRuleRevisions';
import { MultiplexPanelView } from 'interfaces/multiplexPanels';
import { CellTypingPanelSettings, MultiplexPanelCellRules } from 'interfaces/study';
import { isEqual } from 'lodash';
import { CellTypingRequestedOutputs } from '.';
import {
  createPanelConfigurationDraft,
  PanelConfigurationDraft,
  synchronizePanelProbeThresholds,
} from './PanelConfigurationEditor';

export const savedCellTypingPanelSettings = (entries: CellTypingPanelSettings[] | null | undefined, panelId: string) =>
  entries?.find((entry) => entry.panelId === panelId);

export const baselineCellRuleName = (
  panelId: string,
  revisions: MultiplexPanelCellRules[] | null | undefined,
  saved?: CellTypingPanelSettings
) => {
  const panelRevisions = revisionsForPanel(revisions, panelId);
  const matched = saved?.cellRuleRevision
    ? panelRevisions.find((entry) => entry.revision === saved.cellRuleRevision)
    : undefined;
  return (matched ?? panelRevisions[0])?.name?.trim() ?? '';
};

export const baselinePanelConfiguration = (
  panel: MultiplexPanelView,
  sourceDraft: PanelConfigurationDraft | undefined,
  revisions: MultiplexPanelCellRules[] | null | undefined,
  saved?: CellTypingPanelSettings
): PanelConfigurationDraft => {
  if (saved?.configuration && typeof saved.configuration === 'object') {
    return synchronizePanelProbeThresholds(panel, {
      ...createPanelConfigurationDraft(panel),
      ...(saved.configuration as Partial<PanelConfigurationDraft>),
    });
  }
  const draft = sourceDraft ?? createPanelConfigurationDraft(panel);
  const latest = revisionsForPanel(revisions, panel.panelId)[0];
  if (!draft.cellRules.length && latest) {
    const reused = cellRulesFromRevision(latest, panelCellRuleMarkerIds(panel));
    if (!reused.unsupportedReason) {
      return synchronizePanelProbeThresholds(panel, {
        ...draft,
        cellTypeIds: reused.cellTypeIds,
        cellRules: reused.cellRules,
      });
    }
  }
  return synchronizePanelProbeThresholds(panel, draft);
};

export const panelSettingsDiffer = ({
  draft,
  baselineDraft,
  name,
  baselineName,
  requestedOutputs,
  baselineOutputs,
}: {
  draft: PanelConfigurationDraft;
  baselineDraft: PanelConfigurationDraft;
  name: string;
  baselineName: string;
  requestedOutputs: CellTypingRequestedOutputs;
  baselineOutputs: CellTypingRequestedOutputs;
}) =>
  !isEqual(draft, baselineDraft) || name.trim() !== baselineName.trim() || !isEqual(requestedOutputs, baselineOutputs);
